import React from "react";
import type { TenantMeta } from "@packsize/identity-api-sdk";
import { ConfirmationModal } from "@packsize/components";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FormattedMessage, useIntl } from "react-intl";
import { IconButton, Typography } from "@mui/material";
import { useToken } from "@packsize/authentication";

import { type DeleteTenantParams, useDeleteTenant } from "@/hooks/tenant";

interface SiteCardDeleteButtonProps {
	readonly site: TenantMeta;
}

export const SiteCardDeleteButton = ({ site }: SiteCardDeleteButtonProps) => {
	const { decodedToken } = useToken();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const intl = useIntl();
	const { mutate, isPending, error } = useDeleteTenant();

	const closeDeleteConfirmation = (event?: React.SyntheticEvent) => {
		event?.stopPropagation();
		setShowDeleteConfirmation(false);
	};

	const handleDeleteConfirmed = async (event?: React.SyntheticEvent) => {
		event?.stopPropagation();

		if (!site?.id || !decodedToken?.id) {
			return;
		}

		const payload: DeleteTenantParams = {
			tenantId: site.id,
			userId: decodedToken.id,
		};

		mutate(payload, {
			onSuccess: (response) => {
				if (response.success === false && response.status !== 404) {
					return;
				}
				closeDeleteConfirmation();
			},
		});
	};

	const handleIconClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setShowDeleteConfirmation(true);
	};

	return (
		<>
			<IconButton data-testid="site-card__button__trash" onClick={handleIconClick} size="small">
				<DeleteOutlineIcon sx={{ fontSize: 24, color: "text.secondary", cursor: "pointer" }} />
			</IconButton>

			<ConfirmationModal
				size="sm"
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				confirmText={intl.formatMessage({ id: "Confirm", defaultMessage: "Confirm" })}
				onCancel={closeDeleteConfirmation}
				onClose={closeDeleteConfirmation}
				onConfirm={handleDeleteConfirmed}
				open={showDeleteConfirmation}
				title={intl.formatMessage({ id: "Confirm Access Removal", defaultMessage: "Confirm Access Removal" })}
				slotProps={{
					confirmButton: {
						"data-testid": "tenant-remove-confirmation-modal__button__confirm",
						loading: isPending,
					},
					cancelButton: {
						"data-testid": "tenant-remove-confirmation-modal__button__cancel",
					},
				}}
			>
				<Typography>
					<FormattedMessage
						id="If you confirm, you are going to immediately lose access to {tenantName}"
						defaultMessage="If you confirm, you are going to immediately lose access to {tenantName}"
						values={{
							tenantName: site.company,
						}}
					/>
				</Typography>

				{Boolean(error) && (
					<Typography sx={{ marginTop: 2 }} color="error" data-testid="pick-zone-delete-confirmation-modal__error">
						<FormattedMessage
							id="Failed to remove tenant. Please try again."
							defaultMessage="Failed to remove tenant. Please try again."
						/>
					</Typography>
				)}
			</ConfirmationModal>
		</>
	);
};
